import { inject, Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { selectProductsInfo } from '@app/_store/features/products-info/products-info.selectors';
import { ChildBanner } from '@core/models/interfaces/storage-data/banners';
import { TrackEventProperties } from '@core/models/interfaces/storage-data/products-info';
import { MixpanelService } from '@core/services/tracking/mixpanel.service';
import { UserService } from '@core/services/user.service';
import { Store } from '@ngrx/store';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';

/**
 * What every dispatcher of a banner/inbox action hands the handler: the
 * source flags plus the banner whose action fires. (This used to be named
 * `BannerAction`, colliding with the model type it wraps.)
 */
export interface BannerActionContext {
  from: string;
  isBanner: boolean;
  banner: Partial<ChildBanner>;
}

@Injectable({
  providedIn: 'root',
})
export class BannersActionHandlerService {
  private store = inject(Store);
  private router = inject(Router);
  private user = inject(UserService);
  // S4. The three feature management services are resolved through a dynamic
  // import when the banner is clicked (pace.effects pattern) — the core layer
  // keeps no static edge into features, and the services stay root singletons.
  private injector = inject(Injector);
  private toastr = inject(ToastrService);
  private mixpanel = inject(MixpanelService);

  async handleAction(data: BannerActionContext) {
    const action = data.banner.action;
    // an absent action matched no switch case and did nothing — same here (A1.4)
    if (!action) return;

    const view = action.params?.view || action.actionParams?.view;

    switch (action.id || action.actionId) {
      case 'openView':
        if (view === 'billshark' || view === 'billshark/cancellation') {
          this.mixpanel.track(
            'Monetize - Start',
            this.getTrackingProps(data, {
              Type: 'Billshark',
            })
          );

          void this.router.navigate([
            view === 'billshark'
              ? '/settings/lower-your-bills'
              : '/settings/cancel-subscriptions',
          ]);
        } else if (view === 'billshark/fillMissingInfo') {
          void this.router.navigate(['/settings/lower-your-bills'], {
            queryParams: {
              id: 'fillMissingInfo',
              data: action.params?.viewParams,
            },
          });
        } else if (view === 'startUpgradePlan') {
          const opened = this.user.showCheckoutIfNeeded({
            From: `Overview ${data.banner.id}`,
            isBanner: true,
          });
          if (!opened) {
            this.toastr.success('Already on plus.');
          }
        } else if (view === 'createGoal') {
          this.router.navigate(['/goals']).then(async () => {
            const { GoalsManagementService } =
              await import('@app/features/goals/_services/goals-management.service');
            this.injector.get(GoalsManagementService).setUpGoal();
          });
        } else if (view === 'account_management') {
          void this.router.navigate(['/settings/institutions']);
        } else if (view === 'bills') {
          void this.addPaymentFromBanner('bill');
        } else if (view === 'incomes') {
          void this.addPaymentFromBanner('income');
        } else if (view === 'subscriptionsCheckup') {
          void this.router.navigate(['/settings/cancel-subscriptions'], {
            queryParams: {
              id: 'cancelSubscriptions',
            },
          });
        } else if (view === 'categoryDetails') {
          const id = action?.actionParams?.viewParams?.categoryId;
          if (id) {
            void this.navigateToBudgetDetails(id);
          }
        } else if (view === 'savings') {
          void this.router.navigate(['/goals']);
        } else if (view === 'savings_center') {
          void this.router.navigate(['/goals']);
        } else if (view === 'plan/spendingLimits/list') {
          void this.router.navigate(['/budgets']);
        } else if (
          view === 'debtPayoff/plan' ||
          view === 'debtPayoff/schedule'
        ) {
          void this.router.navigate(['/debt-plan']);
        } else if (view === 'transactions' || view === 'transactions/import') {
          void this.router.navigate(['/transactions']);
        } else if (view === 'insights') {
          // POC-8665: the link may carry a report configuration; the insights
          // route applies it from its query parameter and then drops it.
          const viewParams =
            action.params?.viewParams ?? action.actionParams?.viewParams;
          void this.router.navigate(['/insights'], {
            queryParams: viewParams
              ? {
                  action: btoa(
                    JSON.stringify({
                      id: 'openView',
                      params: { view: 'insights', viewParams },
                    })
                  ),
                }
              : {},
          });
        } else if (action?.actionParams?.budgetHash) {
          const id = action?.actionParams?.budgetHash;
          void this.router.navigate(['/budgets', id]);
        }
        break;

      case 'cashAtmTransfer':
      case 'cashAtmDeposit':
        break;

      case 'openSavingProduct':
        const product = ((await firstValueFrom(
          this.store.select(selectProductsInfo)
        )) ?? {})[action.params?.productId ?? ''];
        const productProps = product?.trackEventProperties || {};

        this.mixpanel.track(
          'Monetize - Start',
          (function (props) {
            delete props['Product'];
            return props;
          })(this.getTrackingProps(data, productProps))
        );

        this.mixpanel.track(
          'Monetize - Outclick',
          this.getTrackingProps(data, {
            URL: product.URL,
            ...productProps,
          })
        );

        window.open(product.URL, '_blank', 'noopener,noreferrer');
        break;

      case 'openOffer':
        this.mixpanel.track('Monetize - Start', this.getTrackingProps(data));
        this.mixpanel.track(
          'Monetize - Recommendation',
          this.getTrackingProps(data)
        );
        break;

      case 'openAdvisingURL': {
        const url = action.actionParams?.URL;
        if (!url) break;

        this.mixpanel.track(
          'Monetize - Outclick',
          this.getTrackingProps(data, {
            URL: url,
          })
        );

        window.open(url, '_blank', 'noopener,noreferrer');
        break;
      }
    }
  }

  private getTrackingProps(
    params: BannerActionContext,
    customProps?: Record<string, unknown> | TrackEventProperties
  ): Record<string, unknown> {
    return {
      From: params.from,
      Call2Action: params.banner.title,
      isBadged: false,
      isBanner: params.isBanner != false,
      ...(params.banner.action?.trackEventProperties
        ? params.banner.action.trackEventProperties
        : {}),
      ...(customProps ? customProps : {}),
    };
  }

  private async addPaymentFromBanner(type: 'bill' | 'income'): Promise<void> {
    const { RegularPaymentsManagementService } =
      await import('@app/features/regular-payments/_services/regular-payments-management.service');
    void this.injector
      .get(RegularPaymentsManagementService)
      .addPayment({ type, from: 'Inbox' });
  }

  private async navigateToBudgetDetails(id: string): Promise<void> {
    const { BudgetsManagementService } =
      await import('@app/features/budgets/_services/budgets-management.service');
    this.injector.get(BudgetsManagementService).navigateToBudgetDetails(id);
  }
}
